/*
 * Copyright (C) 2025 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  creatDelayedEmptyStateResource,
  createEmptyStateResource,
  InvalidParam,
  isLoaded,
  setInvalidParamValidationError,
  StateResource,
  ValidationMessageCode,
} from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, UrlSegment } from '@angular/router';
import { catchError, first, Observable, of, tap } from 'rxjs';
import { patchForm } from './form.util';
import { ErrorRepresentation, KeycloakErrorMessage, KeycloakFieldName, KeycloakHttpErrorResponse } from './keycloak-error.model';
import { extractErrorRepresentations, isFieldErrorMessage, isSingleErrorMessage } from './keycloak-error.util';

@Injectable()
export abstract class KeycloakFormService<T> {
  protected readonly formBuilder = inject(FormBuilder);
  protected readonly activatedRoute = inject(ActivatedRoute);

  form: FormGroup;
  _patched: boolean = false;

  _patchConfig: PatchConfig;

  constructor() {
    this.form = this._initForm();

    this._evaluateRoute();
  }

  abstract _initForm(): FormGroup;

  _evaluateRoute(): void {
    this.activatedRoute.url.pipe(first()).subscribe((url: UrlSegment[]) => {
      this._patchConfig = this._buildPatchConfig(url);
    });
  }

  abstract _buildPatchConfig(urlSegments: UrlSegment[]): PatchConfig;

  public get(): Observable<StateResource<T>> {
    return this._patchConfig.doPatch ? this._initLoading() : of(createEmptyStateResource<T>());
  }

  _initLoading(): Observable<StateResource<T>> {
    return this._load(this._patchConfig.id).pipe(tap((stateResource: StateResource<T>) => this._patchIfLoaded(stateResource)));
  }

  abstract _load(id: string): Observable<StateResource<T>>;

  _patchIfLoaded(stateResource: StateResource<T>): void {
    if (isLoaded(stateResource)) this._patch(stateResource.resource);
  }

  public submit(): Observable<StateResource<T>> {
    if (this.form.invalid) {
      return this._processInvalidForm();
    }
    return this.doSubmit().pipe(
      catchError((keycloakError: KeycloakHttpErrorResponse) => this._processResponseValidationErrors(keycloakError)),
    );
  }

  _processInvalidForm(): Observable<StateResource<T>> {
    this._showValidationErrorForAllInvalidControls(this.form);
    return creatDelayedEmptyStateResource<T>();
  }

  _processResponseValidationErrors(keycloakError: KeycloakHttpErrorResponse): Observable<StateResource<T>> {
    try {
      this._setValidationErrorsOnControls(
        this._mapKeycloakErrorRepresentationsToInvalidParams(extractErrorRepresentations(keycloakError)),
      );
    } catch (error: any) {
      console.error(error);
      this._handleUnknownKeycloakError();
    }
    return of(createEmptyStateResource<T>());
  }

  _showValidationErrorForAllInvalidControls(control: AbstractControl): void {
    if (control.invalid) control.updateValueAndValidity();
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach((control) => this._showValidationErrorForAllInvalidControls(control));
    }
  }

  _setValidationErrorsOnControls(invalidParams: InvalidParam[]): void {
    invalidParams.forEach((invalidParam: InvalidParam) => {
      setInvalidParamValidationError(this.form, invalidParam);
    });
  }

  _mapKeycloakErrorRepresentationsToInvalidParams(errorRepresentations: ErrorRepresentation[]): InvalidParam[] {
    return errorRepresentations.map((errorRepresentation: ErrorRepresentation) =>
      this._mapKeycloakErrorRepresentationToInvalidParam(errorRepresentation),
    );
  }

  _mapKeycloakErrorRepresentationToInvalidParam(errorRepresentation: ErrorRepresentation): InvalidParam {
    const controlName: string = this._evaluateControlName(errorRepresentation);
    switch (errorRepresentation.errorMessage) {
      case KeycloakErrorMessage.INVALID_EMAIL:
        return this._mapInvalidEmail(controlName);
      case KeycloakErrorMessage.INVALID_LENGTH:
        return this._mapInvalidLength(controlName, errorRepresentation);
      case KeycloakErrorMessage.EMAIL_EXISTS:
        return this._mapEmailExists(controlName);
      case KeycloakErrorMessage.USERNAME_EXISTS:
        return this._mapUsernameExists(controlName);
      case KeycloakErrorMessage.USERNAME_IS_MISSING:
        return this._mapUserNameIsMissing(controlName);
      default:
        throw new Error(`Unknown keycloak error message ${errorRepresentation.errorMessage}`);
    }
  }

  _mapInvalidEmail(controlName: string): InvalidParam {
    return { name: controlName, reason: ValidationMessageCode.FIELD_INVALID, constraintParameters: [], value: null };
  }

  _mapInvalidLength(controlName: string, errorRepresentation: ErrorRepresentation): InvalidParam {
    return {
      name: controlName,
      reason: ValidationMessageCode.FIELD_SIZE,
      constraintParameters: [
        { name: 'min', value: `${errorRepresentation.params[1]}` },
        { name: 'max', value: `${errorRepresentation.params[2]}` },
      ],
      value: null,
    };
  }

  _mapEmailExists(controlName: string): InvalidParam {
    return {
      name: controlName,
      reason: ValidationMessageCode.FIELD_VALUE_ALREADY_EXISTS,
      constraintParameters: [{ name: 'value', value: 'Email-Adresse' }],
      value: null,
    };
  }

  _mapUsernameExists(controlName: string): InvalidParam {
    return {
      name: controlName,
      reason: ValidationMessageCode.FIELD_VALUE_ALREADY_EXISTS,
      constraintParameters: [{ name: 'value', value: 'Benutzername' }],
      value: null,
    };
  }

  _mapUserNameIsMissing(controlName: string): InvalidParam {
    return {
      name: controlName,
      reason: ValidationMessageCode.FIELD_EMPTY,
      constraintParameters: [],
      value: null,
    };
  }

  _evaluateControlName(errorRepresentation: ErrorRepresentation): string {
    if (isSingleErrorMessage(errorRepresentation)) {
      return this._mapKeycloakErrorMessageToControlName(errorRepresentation.errorMessage);
    }
    if (isFieldErrorMessage(errorRepresentation)) {
      return this._mapKeycloakFieldNameToControlName(errorRepresentation.field);
    }
    throw new Error(`Cannot evaluate control name for error ${errorRepresentation}`);
  }

  abstract _mapKeycloakFieldNameToControlName(keycloakFieldName: KeycloakFieldName): string;

  abstract _mapKeycloakErrorMessageToControlName(keycloakErrorMessage: KeycloakErrorMessage): string;

  abstract _handleUnknownKeycloakError(): void;

  abstract doSubmit(): Observable<StateResource<T>>;

  _patch(valueToPatch: T): void {
    this.form.reset();

    patchForm(valueToPatch, this.form);

    this._patched = true;
  }

  _getFormValue(): T {
    return this.form.value;
  }

  public isPatch(): boolean {
    return this._patched;
  }

  public isInvalid(): boolean {
    return this.form.invalid;
  }

  public getId(): string {
    return this._patchConfig.id;
  }
}

export interface PatchConfig {
  id?: string;
  doPatch: boolean;
}
